import { BackendApiClient } from '../../auth/api/backend-client';
import { BackendValidationError } from '../../auth/api/investor';
import { requireList, UNREADABLE_RESPONSE_CODE } from '../../../shared/lib/response-shape';
import type { ScopeContext } from '../../auth/model/types';

const backendClient = new BackendApiClient();

export interface Portfolio {
  id: string;
  scope_type: string;
  scope_id: string;
  owner_type: string;
  owner_id: string;
  name: string;
  unique_id?: string;
  status: string;
  tags: string[];
  created: string;
}

export interface PortfolioResponse {
  portfolios: Portfolio[];
  cursor?: string;
}

export function accountScopeOf(scopeContext: ScopeContext): ScopeContext | null {
  const accountId = scopeContext.account?.id;
  if (!accountId) {
    return null;
  }
  return { ...scopeContext, scopeId: accountId, scopeType: 'account' };
}

export async function getPortfolios(
  sessionId: string,
  scopeContext: ScopeContext,
  filters?: { ownerId?: string; statuses?: string[]; cursor?: string; limit?: number },
): Promise<PortfolioResponse> {
  const query = new URLSearchParams();
  if (filters?.ownerId) {
    query.set('owner_id', filters.ownerId);
  }
  if (filters?.statuses?.length) {
    query.set('status', filters.statuses.join(','));
  }
  if (filters?.cursor) {
    query.set('cursor', filters.cursor);
  }
  if (filters?.limit) {
    query.set('limit', String(filters.limit));
  }
  const path = query.toString() ? `/portfolio?${query}` : '/portfolio';
  const response = await backendClient.get(path, sessionId, scopeContext);
  if (response.success && response.data) {
    const body = response.data as PortfolioResponse;
    return {
      ...body,
      portfolios: requireList<Portfolio>(body, 'portfolios', 'Unreadable portfolio response'),
    };
  }
  if (response.validationError) {
    throw response.validationError;
  }
  throw new BackendValidationError(response.error || 'Failed to fetch portfolios');
}

const SERVER_PAGE_SIZE = 100;
const MAX_LISTED_PORTFOLIOS = 2000;
const MAX_PORTFOLIO_PAGES = MAX_LISTED_PORTFOLIOS / SERVER_PAGE_SIZE;

export interface PortfolioListing {
  portfolios: Portfolio[];
  isTruncated: boolean;
  isIncomplete: boolean;
}

export async function getAllPortfolios(
  sessionId: string,
  scopeContext: ScopeContext,
  filters?: { ownerId?: string; statuses?: string[]; isCancelled?: () => boolean },
): Promise<PortfolioListing> {
  const { isCancelled, ...pageFilters } = filters ?? {};
  const portfolioById = new Map<string, Portfolio>();
  let cursor: string | undefined;
  for (let page = 0; page < MAX_PORTFOLIO_PAGES; page++) {
    if (isCancelled?.()) {
      return { portfolios: [...portfolioById.values()], isTruncated: false, isIncomplete: true };
    }
    let response: PortfolioResponse;
    try {
      response = await getPortfolios(sessionId, scopeContext, {
        ...pageFilters, cursor, limit: SERVER_PAGE_SIZE,
      });
    } catch (error) {
      if (portfolioById.size === 0) {
        throw error;
      }
      return { portfolios: [...portfolioById.values()], isTruncated: false, isIncomplete: true };
    }
    for (const portfolio of response.portfolios) {
      portfolioById.set(portfolio.id, portfolio);
    }
    cursor = response.cursor || undefined;
    if (!cursor) {
      return { portfolios: [...portfolioById.values()], isTruncated: false, isIncomplete: false };
    }
  }
  return { portfolios: [...portfolioById.values()], isTruncated: true, isIncomplete: false };
}

export async function getPortfolio(
  sessionId: string,
  scopeContext: ScopeContext,
  portfolioId: string,
): Promise<Portfolio | null> {
  const response = await backendClient.get(`/portfolio?ids=${portfolioId}`, sessionId, scopeContext);
  if (response.success && response.data) {
    const portfolios = requireList<Portfolio>(
      response.data, 'portfolios', 'Unreadable portfolio response',
    );
    return portfolios[0] || null;
  }
  if (response.validationError) {
    throw response.validationError;
  }
  throw new BackendValidationError(response.error || 'Failed to fetch portfolio');
}

export async function patchPortfolios(
  sessionId: string,
  scopeContext: ScopeContext,
  updates: { id: string; status: 'active' | 'blocked' }[],
): Promise<Portfolio[]> {
  const body = { portfolios: updates };
  const response = await backendClient.patch('/portfolio', sessionId, body, scopeContext);
  if (response.success && response.data) {
    const portfolios = requireList<Portfolio>(
      response.data, 'portfolios', 'Unreadable portfolio response',
    );
    if (portfolios.length !== updates.length) {
      throw new BackendValidationError(
        'Portfolio update did not answer for every portfolio', UNREADABLE_RESPONSE_CODE,
      );
    }
    return portfolios;
  }
  if (response.validationError) {
    throw response.validationError;
  }
  throw new BackendValidationError(response.error || 'Failed to update portfolios');
}

export async function createPortfolio(
  sessionId: string,
  scopeContext: ScopeContext,
  params: { name: string; ownerId: string; uniqueId: string },
): Promise<Portfolio> {
  const body = {
    portfolios: [{
      name: params.name,
      owner_type: 'investor',
      owner_id: params.ownerId,
      unique_id: params.uniqueId,
    }],
  };
  const response = await backendClient.post('/portfolio', sessionId, body, scopeContext);
  if (response.success && response.data) {
    const portfolios = requireList<Portfolio>(
      response.data, 'portfolios', 'Unreadable portfolio response',
    );
    if (portfolios.length === 0) {
      throw new BackendValidationError(
        'Portfolio creation returned no portfolio', UNREADABLE_RESPONSE_CODE,
      );
    }
    return portfolios[0];
  }
  if (response.validationError) {
    throw response.validationError;
  }
  throw new BackendValidationError(response.error || 'Failed to create portfolio');
}
